import React, { useCallback } from 'react';
import CreditsBadge from '../components/CreditsBadge';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { USER_TYPES } from '../constants/designer';
import UserMenu from '../components/UserMenu';
import NavBrand from '../components/NavBrand';
import HeaderSearch from '../components/HeaderSearch';
import NotificationBell from '../components/NotificationBell';
import MessagesButton from '../components/MessagesButton';
import BackBar from '../components/BackBar';
import DesignerProfileHeader from '../components/DesignerProfileHeader';
import CustomerProfileHeader from '../components/CustomerProfileHeader';
import DashboardGallery from '../components/DashboardGallery';
import Workspace from './Workspace';
import './SearchPage.css';
import './Dashboard.css';

// ── PROFILE ──────────────────────────────────────────────────────────────────
// "You", as a page. Reached from the profile tab in the mobile bar.
//
// It exists because a customer had nowhere that was theirs: a designer's
// identity lives at the top of the dashboard, but a customer's dashboard is a
// set of get-started cards with no picture, no name and nothing to edit.
//
// EVERY ACCOUNT TYPE, one route. A designer or a shop gets their full header
// and their published work — the same thing visitors see — so the profile tab
// shows them their profile rather than a lesser copy of it. A customer gets the
// slim version: picture, name, and the three places they go from here.
export default function Profile() {
  const { user, profile } = useAuth();
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  const isDesigner = profile?.userType === USER_TYPES.DESIGNER;
  const isShop     = profile?.userType === USER_TYPES.SHOP;
  // The two roles with a public page and a gallery to manage. Everything this
  // page does differently for a designer, it does identically for a shop —
  // except the 3D tab, which a shop has no way to fill.
  const publishes = isDesigner || isShop;

  // Dropped once the gallery has acted on it, so the parameter does not stick
  // around in anything shared or bookmarked — and so clicking the SAME
  // notification twice opens the item again rather than being a no-op.
  const clearDeepLink = useCallback(() => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('photo');
      next.delete('design');
      return next;
    }, { replace: true });
  }, [setParams]);

  return (
    <div className="dash-root">
      {/* `--settings` keeps the gear in the header here and nowhere else on a
          phone — see MobileTabBar.css. */}
      <header className="dash-nav dash-nav--settings">
        <NavBrand />
        <div className="dash-nav-right">
          <HeaderSearch />
          <NotificationBell />
          <CreditsBadge />
          <MessagesButton />
          {user ? <UserMenu /> : <Link to="/login" className="sp-signin">{t('nav_login')}</Link>}
        </div>
      </header>

      <BackBar wide hideOnMobile />

      <main className="dash-main dash-main--wide">
        {publishes ? <DesignerProfileHeader /> : <CustomerProfileHeader />}
        {/* Their own published work, managed in place. A customer has none —
            the gallery belongs to accounts with a public page — so it is
            simply absent for them.

            ?photo= / ?design= arrive from a "someone liked your work"
            notification and open that item here. */}
        {publishes && (
          <DashboardGallery
            openPhotoId={params.get('photo')}
            openDesignId={params.get('design')}
            onOpened={clearDeepLink}
            photosOnly={isShop}
          />
        )}

        {/* THE WORKSPACE, FOR A CUSTOMER — in place, not behind a link.
            Their projects are the only thing they have here, and this page was
            a name and a picture above an empty screen without them. A designer
            keeps the separate /workspace page: their profile is already full,
            with the published work that visitors see, and their projects are a
            working area rather than the thing the page is about. */}
        {/* A SHOP IS NOT GIVEN THIS EITHER. The embedded workspace is here
            because a customer's projects are the only thing they have on this
            page; a shop's page is already full of its products, and a shop has
            no 3D projects to show. */}
        {!publishes && <Workspace embedded />}
      </main>
    </div>
  );
}
